import { cn } from "@/lib/utils";

interface SkeletonProps extends React.ComponentProps<"div"> {
  /** Shape variant */
  variant?: "text" | "circle" | "rect";
}

function Skeleton({ className, variant = "rect", ...props }: SkeletonProps) {
  return (
    <div
      data-slot="skeleton"
      aria-hidden="true"
      className={cn(
        "bg-[var(--color-bg-muted)] animate-shimmer bg-[length:200%_100%]",
        variant === "text" && "h-4 w-full rounded-[var(--radius-sm)]",
        variant === "circle" && "rounded-full",
        variant === "rect" && "rounded-[var(--radius-md)]",
        className
      )}
      {...props}
    />
  );
}

/* ── Preset skeletons ───────────────────────────────────────── */

/** Card-shaped skeleton */
function SkeletonCard({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "rounded-[var(--radius-xl)] border border-[var(--color-border-subtle)]",
        "bg-[var(--color-bg-card)] p-[var(--space-5)] space-y-[var(--space-3)]",
        className
      )}
      aria-hidden="true"
    >
      <Skeleton className="h-5 w-1/3" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-2/3" />
    </div>
  );
}

/** List row skeleton */
function SkeletonRow({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "flex items-center gap-[var(--space-3)] py-[var(--space-3)]",
        className
      )}
      aria-hidden="true"
    >
      <Skeleton variant="circle" className="size-10" />
      <div className="flex-1 space-y-[var(--space-2)]">
        <Skeleton className="h-4 w-1/2" />
        <Skeleton className="h-3 w-1/4" />
      </div>
    </div>
  );
}

/** Avatar skeleton */
function SkeletonAvatar({
  className,
  size = "md",
}: {
  className?: string;
  size?: "sm" | "md" | "lg";
}) {
  const sizeClass =
    size === "sm" ? "size-7" : size === "lg" ? "size-12" : "size-9";
  return (
    <Skeleton
      variant="circle"
      className={cn(sizeClass, className)}
    />
  );
}

export { Skeleton, SkeletonCard, SkeletonRow, SkeletonAvatar };
export type { SkeletonProps };
